import { useMemo, useState } from 'react'
import { errorHandler } from '../../../../../services/util/error.service'
import * as ResumoHttp from '../../../../../services/api/https/picking/resumo-separcao.http'

export const LOAD_MESSAGE = {
  NONE: 'NONE',
  ASSOCIANDO: 'Associando ocorrencias',
  SUCCESS: 'Ocorrências associadas com sucesso',
  FILE_UPLOADED: 'Foto anexada',
  RELOADING: 'Recarregando faltas...',
}

const INITIAL_STATE = {
  activeIndex: 0,
  btnAssociarDisabled: false,
  selectedPicking: null,
  errorMessage: null,
  loadingMessage: LOAD_MESSAGE.NONE,
  faltasItems: null,
  faltaSelected: null,
  modal: { show: false, data: {} },
  desdobramentoModal: { show: false, falta: null },
  ajusteOcorrenciaModal: { show: false, falta: null },
}

export default function useFaltas() {
  const [state, setState] = useState(INITIAL_STATE)

  const localDispatch = useMemo(() => {
    //Helpers
    const checkOcorrencias = items => {
      items.forEach(i => {
        const result = i.faltas.find(f => f.missing_reason === '')
        if (result) {
          setState(prevState => ({
            ...prevState,
            errorMessage: `Existe(m) faltas sem ocorrência associada. Cliente ${i.company_code} - ${i.nome_fantasia}`,
          }))
        }
      })
    }

    const buildDataFormatted = items => {
      let result = []
      items.forEach(i => {
        const r = i.faltas.map(f => {
          return {
            falta_id: f.falta_id,
            missing_reason_id: f.missing_reason.id,
          }
        })
        result = [...result, ...r]
      })
      return result
    }

    const checkIfBtnAssociarIsDisabled = selectedPicking => {
      return selectedPicking ? selectedPicking.status !== 'Picking Concluido' : false
    }

    return {
      // Actions
      associateOcorrencias(faltasItems) {
        checkOcorrencias(faltasItems)
        const data = buildDataFormatted(faltasItems)
        ResumoHttp.assocFaltaOcorrencia(data)
          .then(() =>
            setState(prevState => ({ ...prevState, loadingMessage: LOAD_MESSAGE.SUCCESS })),
          )
          .catch(e => setState(prevState => ({ ...prevState, errorMessage: errorHandler(e) })))
      },

      resetState() {
        setState(prevState => ({
          ...prevState,
          loadingMessage: LOAD_MESSAGE.NONE,
          errorMessage: null,
        }))
      },

      // Events
      onInit(selectedPicking, faltasItems) {
        setState(prevState => ({
          ...prevState,
          selectedPicking: selectedPicking,
          faltasItems: faltasItems,
          btnAssociarDisabled: checkIfBtnAssociarIsDisabled(selectedPicking),
        }))
      },

      onTabChange(e) {
        setState(prevState => ({ ...prevState, activeIndex: e.index }))
      },

      onDropdownChange(falta, ocorrencia) {
        setState(prevState => {
          prevState.faltasItems.forEach(i => {
            const index = i.faltas.findIndex(f => f.falta_id === falta.falta_id)
            if (index !== -1) {
              i.faltas[index].missing_reason = ocorrencia
            }
          })
          return { ...prevState }
        })
      },

      onBtnClicked() {
        setState(prevState => ({ ...prevState, loadingMessage: LOAD_MESSAGE.ASSOCIANDO }))
      },

      onVerFotosClicked(missing_product, item) {
        setState(prevState => ({
          ...prevState,
          modal: { show: true, data: { fotos: missing_product.files } },
          itemSelected: item,
          faltaSelected: missing_product,
        }))
      },

      onModalClose() {
        setState(prevState => ({ ...prevState, modal: { show: false, data: {} } }))
      },

      onDesdobrarFaltaClicked(falta) {
        setState(prevState => ({
          ...prevState,
          desdobramentoModal: { show: true, falta },
        }))
      },

      onDesdobramentoModalClose() {
        setState(prevState => ({
          ...prevState,
          desdobramentoModal: { show: false, falta: null },
        }))
      },

      onAjusteOcorrenciaClicked(falta) {
        setState(prevState => ({
          ...prevState,
          ajusteOcorrenciaModal: { show: true, falta },
        }))
      },

      onAjusteOcorrenciaModalClose() {
        setState(prevState => ({
          ...prevState,
          ajusteOcorrenciaModal: { show: false, falta: null },
        }))
      },

      onReloadAfterDesdobramento(selectedPicking) {
        // Re-trigger init to reload faltasItems from the parent (ResumoSeparacao will re-fetch)
        // The parent state will be updated via getResumoInfo which is triggered externally
        // For now, just close the modal; the parent should re-fetch if needed.
        setState(prevState => ({
          ...prevState,
          desdobramentoModal: { show: false, falta: null },
        }))
      },

      async onDeleteFoto(foto, selectedPicking) {
        try {
          await Promise.all([
            ResumoHttp.deleteMissingProductImage(selectedPicking.id, foto.id),
            ResumoHttp.deleteMissingProductFile(foto.url_image.split('/').pop()),
          ])

          setState(prevState => {
            const index = prevState.faltasItems.findIndex(
              i => i.cnpj === prevState.itemSelected.cnpj,
            )
            if (index !== -1) {
              const faltaIndex = prevState.faltasItems[index].faltas.findIndex(
                f => f.falta_id === prevState.faltaSelected.falta_id,
              )
              if (faltaIndex !== -1)
                prevState.faltasItems[index].faltas[faltaIndex].files = prevState.faltasItems[
                  index
                ].faltas[faltaIndex].files.filter(f => f.id !== foto.id)
            }
            return { ...prevState }
          })
        } catch (error) {
          setState(prevState => ({ ...prevState, errorMessage: error.message }))
        }
      },

      async onUploadCompleted(file_names, falta, faltaItem, selectedPicking) {
        const savedFiles = await ResumoHttp.postFotosFalta(
          selectedPicking,
          falta.falta_id,
          file_names,
        )

        setState(prevState => {
          const index = prevState.faltasItems.findIndex(i => i.cnpj === faltaItem.cnpj)
          if (index !== -1) {
            const faltaIndex = prevState.faltasItems[index].faltas.findIndex(
              f => f.falta_id === falta.falta_id,
            )
            if (faltaIndex !== -1)
              prevState.faltasItems[index].faltas[faltaIndex].files = [
                ...prevState.faltasItems[index].faltas[faltaIndex].files,
                ...savedFiles,
              ]
          }
          return { ...prevState }
        })
      },
    }
  }, [])

  return { state, localDispatch }
}
